import React, { useState, useEffect } from "react";
import { useApp } from "../contexts/AppContext";
import { useToast } from "../contexts/ToastContext";
import {
  Zap,
  FileText,
  Banknote,
  Smartphone,
  User,
  Phone,
  Users,
  Clock,
  IndianRupee,
} from "lucide-react";
import { cn } from "../lib/utils";
import {
  server_post_data,
  save_funzone_billing,
} from "../ServiceConnection/serviceconnection.js";
import {
  loadFunZonePrinterSettings,
  printFunZoneTicket,
} from "../ServiceConnection/FunZonePrinter.js";
import { get_funzone_settings } from "../ServiceConnection/serviceconnection.js";

/* ── Shared UI ─────────────────────────────────── */

const InputField = ({ label, Icon, error, children }) => (
  <div className="space-y-1.5">
    <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">
      {Icon && <Icon size={13} />} {label}
    </label>
    {children}
    {error && <p className="text-xs text-red-500 mt-0.5">{error}</p>}
  </div>
);

const inputCls =
  "w-full rounded-xl border border-gray-200 px-3 py-3 text-sm text-gray-900 outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500";
const inputErrCls =
  "w-full rounded-xl border border-red-400 px-3 py-3 text-sm text-gray-900 outline-none focus:border-red-500 focus:ring-1 focus:ring-red-400";

const PaymentSelector = ({ value, onChange }) => (
  <div className="space-y-2">
    <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
      Payment Mode
    </label>
    <div className="grid grid-cols-2 gap-3">
      {[
        ["cash", "Cash", Banknote],
        ["upi", "UPI", Smartphone],
        ["split", "Split Payment", IndianRupee],
      ].map(([v, l, Ic]) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          className={cn(
            "flex items-center justify-center gap-2 rounded-xl border-2 px-4 py-3 text-sm font-bold transition-all",
            value === v
              ? "border-purple-600 bg-purple-50 text-purple-600"
              : "border-gray-200 text-gray-500 hover:border-purple-300",
          )}
        >
          <Ic size={18} /> {l}
        </button>
      ))}
    </div>
  </div>
);

/* ── Main Component ────────────────────────────── */

export default function BillingPage() {
  const { settings, tickets, updateSettings } = useApp();
  const { toast } = useToast();

  const defaultForm = () => ({
    kidName: "",
    parentMobile: "",
    numberOfKids: 1,
    duration: settings.defaultDuration,
    pricePerHour: settings.defaultPricePerHour,
    paymentMode: "cash",
    cashAmount: "",
    upiAmount: "",
  });

  const [form, setForm] = useState(defaultForm);
  // NEW: dynamic kids array
  const [kidNames, setKidNames] = useState([""]);
  const [fast, setFast] = useState({
    mobile: "",
    paymentMode: "cash",
    cashAmount: "",
    upiAmount: "",
  });
  const [activeTab, setActiveTab] = useState("normal");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [fastErrors, setFastErrors] = useState({});

  useEffect(() => {
    loadFunZonePrinterSettings();
    fetchDefaultSettings();
  }, []);

  const fetchDefaultSettings = async () => {
    try {
      const response = await server_post_data(
        get_funzone_settings,
        new FormData(),
      );
      if (response.data.status === 1) {
        const data = response.data.data;
        const price =
          data.default_price_per_hour || settings.defaultPricePerHour;
        const duration =
          data.default_duration_minutes || settings.defaultDuration;
        updateSettings({
          defaultPricePerHour: price,
          defaultDuration: duration,
          gstPercentage: data.gst_percentage || 0,
        });
        setForm((p) => ({ ...p, pricePerHour: price, duration }));
      }
    } catch (err) {
      console.error("Settings fetch error", err);
    }
  };

  /* helpers */
  const setF = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));
  // const setFv = (k) => (e) => {
  //   setForm((p) => ({ ...p, [k]: e.target.value }));
  //   setErrors((p) => ({ ...p, [k]: "" }));
  // };

  // const setFn = (k) => (e) => {
  //   setForm((p) => ({ ...p, [k]: Number(e.target.value) }));
  //   setErrors((p) => ({ ...p, [k]: "" }));
  // };

  const cleanNumeric = (value, min = 0, max = null) => {
    if (value === "" || value === null) return "";

    // Remove all non-numeric characters
    let v = value.toString().replace(/\D+/g, "");

    // Remove leading zeros
    v = v.replace(/^0+(?=\d)/, "");

    if (v === "") v = "0";

    // Convert to number
    v = Number(v);

    // Apply minimum
    if (v < min) v = min;

    // Apply maximum if provided
    if (max !== null && v > max) v = max;

    return v;
  };

  const isDup = (mob) =>
    tickets.some((t) => t.isActive && t.parentMobile === mob);
  const validMob = (m) => /^\d{10}$/.test(m);

  // NEW — only alphabets allowed in names
  const validName = (str) => /^[A-Za-z ]+$/.test(str);

  // NEW — mobile accepts only numbers
  const digitsOnly = (str) => /^[0-9]*$/.test(str);

  /* ── VALIDATION ── */
  const validateNormal = () => {
    const e = {};
    if (!form.parentName || !form.parentName.trim())
      e.parentName = "Parent name is required";
    else if (!validName(form.parentName)) e.parentName = "Only letters allowed";
    kidNames.forEach((n, i) => {
      if (!n.trim()) e[`kid_${i}`] = `Kid ${i + 1} name required`;
      else if (!validName(n)) e[`kid_${i}`] = "Only letters allowed";
    });
    if (!form.parentMobile) e.parentMobile = "Mobile number is required";
    else if (!validMob(form.parentMobile))
      e.parentMobile = "Enter valid 10-digit mobile";
    else if (isDup(form.parentMobile))
      e.parentMobile = "Active ticket exists for this mobile";
    if (!form.numberOfKids || form.numberOfKids < 1)
      e.numberOfKids = "Min 1 kid required";
    // if (!form.numberOfKids || form.numberOfKids < 10)
    //   e.numberOfKids = "Max 10 kid required";
    if (!form.duration || form.duration < 15)
      e.duration = "Min 15 minutes required";
    if (!form.pricePerHour || form.pricePerHour <= 0)
      e.pricePerHour = "Enter valid price";
    if (form.paymentMode === "split") {
      const cash = Number(form.cashAmount || 0);
      const upi = Number(form.upiAmount || 0);

      if (cash <= 0) e.cashAmount = "Cash amount required";

      if (upi <= 0) e.upiAmount = "UPI amount required";

      if (cash + upi !== total) e.upiAmount = `Cash + UPI must equal ₹${total}`;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateFast = () => {
    const e = {};
    if (!fast.parentName || !fast.parentName.trim())
      e.parentName = "Parent name is required";
    else if (!validName(fast.parentName)) e.parentName = "Only letters allowed";
    if (!fast.mobile) e.mobile = "Mobile number is required";
    else if (!validMob(fast.mobile)) e.mobile = "Enter valid 10-digit mobile";
    else if (isDup(fast.mobile))
      e.mobile = "Active ticket exists for this mobile";
    if (fast.paymentMode === "split") {
      const cash = Number(fast.cashAmount || 0);
      const upi = Number(fast.upiAmount || 0);

      if (cash <= 0) e.cashAmount = "Cash amount required";

      if (upi <= 0) e.upiAmount = "UPI amount required";

      if (cash + upi !== fastTotal)
        e.upiAmount = `Cash + UPI must equal ₹${fastTotal}`;
    }
    setFastErrors(e);
    return Object.keys(e).length === 0;
  };

  const total = Math.round(
    (form.duration / 60) * form.pricePerHour * form.numberOfKids,
  );
  const fastTotal = Math.round(
    (settings.defaultDuration / 60) * settings.defaultPricePerHour,
  );

  const gstRate = settings.gstPercentage || 0;

  // Normal Billing
  const subTotal =
    gstRate > 0
      ? Number((total / (1 + gstRate / 100)).toFixed(2))
      : total;

  const gstAmount = Number((total - subTotal).toFixed(2));
  const cgstAmount = Number((gstAmount / 2).toFixed(2));
  const sgstAmount = Number((gstAmount / 2).toFixed(2));

  // Fast Billing
  const fastSubTotal =
    gstRate > 0
      ? Number((fastTotal / (1 + gstRate / 100)).toFixed(2))
      : fastTotal;

  const fastGstAmount = Number((fastTotal - fastSubTotal).toFixed(2));
  const fastCgstAmount = Number((fastGstAmount / 2).toFixed(2));
  const fastSgstAmount = Number((fastGstAmount / 2).toFixed(2));

  /* ── NORMAL BILLING ── */
  const handleNormal = async (e) => {
    e.preventDefault();
    if (!validateNormal()) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("main_id", "0");
      formData.append("billing_type", "NORMAL");
      formData.append("parent_name", form.parentName);
      formData.append("parent_mobile", form.parentMobile);
      // formData.append("kid_name", form.kidName);
      formData.append("kid_names", JSON.stringify(kidNames));
      formData.append("no_of_kids", form.numberOfKids);
      formData.append("duration_minutes", form.duration);
      formData.append("rate_per_hour", form.pricePerHour);
      formData.append("payment_mode", form.paymentMode);
      formData.append(
        "cash_amount",
        form.paymentMode === "split"
          ? form.cashAmount
          : form.paymentMode === "cash"
            ? total
            : 0,
      );

      formData.append(
        "upi_amount",
        form.paymentMode === "split"
          ? form.upiAmount
          : form.paymentMode === "upi"
            ? total
            : 0,
      );

      const response = await server_post_data(save_funzone_billing, formData);

      if (response.data.status === 1) {
        toast({
          title: "Ticket Created ✅",
          description: "Billing saved successfully",
        });
        printFunZoneTicket({
          ...response.data.billing_data,
          venue_name: "Fun Zone",
        });
        setForm(defaultForm());
        setErrors({});
      } else {
        toast({
          title: "Error ❌",
          description: response.data.message || "Failed to create ticket",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error ❌",
        description: "Server error. Try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  /* ── FAST TRACK BILLING ── */
  const handleFast = async (e) => {
    e.preventDefault();
    if (!validateFast()) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("main_id", "0");
      formData.append("billing_type", "FAST_TRACK");
      formData.append("parent_name", fast.parentName);
      formData.append("parent_mobile", fast.mobile);
      formData.append("payment_mode", fast.paymentMode);
      formData.append(
        "cash_amount",
        fast.paymentMode === "split"
          ? fast.cashAmount
          : fast.paymentMode === "cash"
            ? fastTotal
            : 0,
      );

      formData.append(
        "upi_amount",
        fast.paymentMode === "split"
          ? fast.upiAmount
          : fast.paymentMode === "upi"
            ? fastTotal
            : 0,
      );
      formData.append("no_of_kids", 1);
      formData.append("duration_minutes", settings.defaultDuration);
      formData.append("rate_per_hour", settings.defaultPricePerHour);

      const response = await server_post_data(save_funzone_billing, formData);

      if (response.data.status === 1) {
        toast({
          title: "⚡ Fast Ticket Created",
          description: "Billing saved successfully",
        });
        printFunZoneTicket({
          ...response.data.billing_data,
          venue_name: "Fun Zone",
        });
        setFast({ mobile: "", paymentMode: "cash" });
        setFastErrors({});
      } else {
        toast({
          title: "Error ❌",
          description: response.data.message || "Failed to create ticket",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error ❌",
        description: "Server error. Try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const TabBtn = ({ id, label, Icon }) => (
    <button
      type="button"
      onClick={() => setActiveTab(id)}
      className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
        activeTab === id
          ? "bg-purple-600 text-white"
          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
      }`}
    >
      <Icon size={15} /> {label}
    </button>
  );

  /* ── RENDER ── */
  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-gray-900">
          Point of Sale
        </h1>
        <p className="text-gray-500">Create new tickets for kids zone entry</p>
      </div>

      <div className="mb-6 flex gap-2">
        <TabBtn id="normal" label="Normal" Icon={FileText} />
        <TabBtn id="fast" label="Fast Track" Icon={Zap} />
      </div>

      {/* ── NORMAL TAB ── */}
      {activeTab === "normal" ? (
        <form onSubmit={handleNormal}>
          <div className="grid gap-6 lg:grid-cols-5">
            <div className="lg:col-span-3 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-5">
              <h2 className="font-display text-lg font-bold text-gray-900">
                Booking Details
              </h2>

              {/* <div className="grid grid-cols-2 gap-4">
                <InputField
                  label="Duration (min)"
                  Icon={Clock}
                  error={errors.duration}
                >
                  <input
                    type="number"
                    min={15}
                    value={form.duration}
                    onChange={(e) => {
                      const v = cleanNumeric(e.target.value, 15);
                      setForm((p) => ({ ...p, duration: v }));
                    }}
                    className={`${errors.duration ? inputErrCls : inputCls} text-center font-bold`}
                  />
                </InputField>
                <InputField
                  label="Per Hour (₹)"
                  Icon={IndianRupee}
                  error={errors.pricePerHour}
                >
                  <input
                    type="number"
                    min={0}
                    value={form.pricePerHour}
                    onChange={(e) => {
                      const v = cleanNumeric(e.target.value, 1);
                      setForm((p) => ({ ...p, pricePerHour: v }));
                    }}
                    className={`${errors.pricePerHour ? inputErrCls : inputCls} text-center font-bold`}
                  />
                </InputField>
              </div> */}
              {/* <div className="space-y-3">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Kids Name
                </label>

                <div className="space-y-3 max-h-56 overflow-y-auto pr-2">
                  {kidNames.map((name, idx) => (
                    <div key={idx} className="space-y-1.5">
                      <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">
                        <User size={13} /> Kid {idx + 1}
                      </label>

                      <input
                        value={kidNames[idx]}
                        onChange={(e) => {
                          if (validName(e.target.value) || e.target.value === "") {
                            const arr = [...kidNames];
                            arr[idx] = e.target.value;
                            setKidNames(arr);
                          }
                        }}
                        placeholder={`Enter Kid ${idx + 1} Name`}
                        className={inputCls}
                      />

                      {errors[`kid_${idx}`] && (
                        <p className="text-xs text-red-500 mt-0.5">
                          {errors[`kid_${idx}`]}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div> */}
              <div className="grid grid-cols-3 gap-4">
                <InputField
                  label="Kids"
                  Icon={Users}
                  error={errors.numberOfKids}
                >
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={form.numberOfKids}
                    onChange={(e) => {
                      const val = cleanNumeric(e.target.value, 1);
                      setForm((p) => ({ ...p, numberOfKids: val }));
                      // const val = Number(e.target.value);
                      // setForm((p) => ({ ...p, numberOfKids: val }));
                      // setErrors((p) => ({ ...p, numberOfKids: "" }));

                      // NEW → auto adjust kid name fields
                      if (val > kidNames.length) {
                        setKidNames([
                          ...kidNames,
                          ...Array(val - kidNames.length).fill(""),
                        ]);
                      } else {
                        setKidNames(kidNames.slice(0, val));
                      }
                    }}
                    className={`${errors.numberOfKids ? inputErrCls : inputCls} text-center font-bold`}
                  />
                </InputField>
                <InputField
                  label="Duration (min)"
                  Icon={Clock}
                  error={errors.duration}
                >
                  <input
                    type="number"
                    // min={15}
                    value={form.duration}
                    // onChange={setFn("duration")}
                    onChange={(e) => {
                      const v = cleanNumeric(e.target.value, 0);
                      setForm((p) => ({ ...p, duration: v }));
                    }}
                    className={`${errors.duration ? inputErrCls : inputCls} text-center font-bold`}
                  />
                </InputField>
                <InputField
                  label="Per Hour (₹)"
                  Icon={IndianRupee}
                  error={errors.pricePerHour}
                >
                  <input
                    type="number"
                    min={0}
                    value={form.pricePerHour}
                    // onChange={setFn("pricePerHour")}
                    onChange={(e) => {
                      const v = cleanNumeric(e.target.value, 1);
                      setForm((p) => ({ ...p, pricePerHour: v }));
                    }}
                    className={`${errors.pricePerHour ? inputErrCls : inputCls} text-center font-bold`}
                  />
                </InputField>
              </div>
              <div className="space-y-3">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Kids Name
                </label>

                <div className="space-y-3 max-h-56 overflow-y-auto pr-2">
                  {kidNames.map((name, idx) => (
                    <div key={idx} className="space-y-1.5">
                      <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">
                        <User size={13} /> Kid {idx + 1}
                      </label>

                      <input
                        value={kidNames[idx]}
                        onChange={(e) => {
                          if (
                            validName(e.target.value) ||
                            e.target.value === ""
                          ) {
                            const arr = [...kidNames];
                            arr[idx] = e.target.value;
                            setKidNames(arr);
                          }
                        }}
                        placeholder={`Enter Kid ${idx + 1} Name`}
                        className={inputCls}
                      />

                      {errors[`kid_${idx}`] && (
                        <p className="text-xs text-red-500 mt-0.5">
                          {errors[`kid_${idx}`]}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              {/* <InputField label="Kid Name" Icon={User} error={errors.kidName}>
                <input
                  value={form.kidName}
                  onChange={setFv("kidName")}
                  placeholder="Enter kid's name"
                  className={errors.kidName ? inputErrCls : inputCls}
                />
              </InputField> */}
              <InputField
                label="Parent Name"
                Icon={User}
                error={errors.parentName}
              >
                <input
                  value={form.parentName || ""}
                  onChange={(e) => {
                    if (validName(e.target.value) || e.target.value === "")
                      setForm((p) => ({ ...p, parentName: e.target.value }));
                  }}
                  placeholder="Enter parent name"
                  className={inputCls}
                />
              </InputField>
              <InputField
                label="Parent Mobile"
                Icon={Phone}
                error={errors.parentMobile}
              >
                <input
                  value={form.parentMobile}
                  onChange={(e) => {
                    if (digitsOnly(e.target.value)) {
                      setForm((p) => ({ ...p, parentMobile: e.target.value }));
                    }
                  }}
                  placeholder="10-digit mobile number"
                  maxLength={10}
                  className={errors.parentMobile ? inputErrCls : inputCls}
                />
              </InputField>
            </div>

            <div className="lg:col-span-2 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm flex flex-col gap-5 self-start h-fit">
              <h2 className="font-display text-lg font-bold text-gray-900">
                Order Summary
              </h2>

              <div className="space-y-3 text-sm">
                {[
                  ["Rate", `₹${form.pricePerHour}/hr`],
                  ["Duration", `${form.duration} min`],
                  ["No. of kids", `×${form.numberOfKids}`],
                ].map(([l, v]) => (
                  <div key={l} className="flex justify-between text-gray-500">
                    <span>{l}</span>
                    <span>{v}</span>
                  </div>
                ))}
                <div className="flex justify-between text-gray-500">
                  <span>Subtotal(excluding GST)</span>
                  <span>₹{subTotal.toFixed(2)}</span>
                </div>

                <div className="flex justify-between text-gray-500">
                  <span>CGST ({gstRate / 2}%)</span>
                  <span>₹{cgstAmount.toFixed(2)}</span>
                </div>

                <div className="flex justify-between text-gray-500">
                  <span>SGST ({gstRate / 2}%)</span>
                  <span>₹{sgstAmount.toFixed(2)}</span>
                </div>

                {/* <div className="flex justify-between text-gray-500 font-medium">
                  <span>Total GST ({gstRate}%)</span>
                  <span>₹{gstAmount.toFixed(2)}</span>
                </div> */}
                <div className="border-t border-dashed border-gray-200" />
                <div className="flex items-center justify-between">
                  <span className="font-display text-base font-bold text-gray-900">
                    Final amount (inclusive of GST)
                  </span>
                  <span className="font-display text-3xl font-bold text-purple-600">
                    ₹{total.toLocaleString()}
                  </span>
                </div>
              </div>

              <PaymentSelector
                value={form.paymentMode}
                onChange={setF("paymentMode")}
              />
              {form.paymentMode === "split" && (
                <div className="grid grid-cols-2 gap-3">
                  <InputField label="Cash Amount" error={errors.cashAmount}>
                    <input
                      type="number"
                      value={form.cashAmount}
                      onChange={(e) => {
                        const cash = cleanNumeric(e.target.value, 0);

                        setForm((p) => ({
                          ...p,
                          cashAmount: cash,
                          upiAmount: Math.max(total - cash, 0),
                        }));
                      }}
                      className={errors.cashAmount ? inputErrCls : inputCls}
                    />
                  </InputField>

                  <InputField label="UPI Amount" error={errors.upiAmount}>
                    <input
                      type="number"
                      value={form.upiAmount}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          upiAmount: e.target.value,
                        }))
                      }
                      className={errors.upiAmount ? inputErrCls : inputCls}
                    />
                  </InputField>
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="mt-auto h-14 w-full rounded-xl bg-purple-600 text-base font-bold text-white hover:bg-purple-700 transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Creating..." : "Generate Ticket"}
              </button>
            </div>
          </div>
        </form>
      ) : (
        /* ── FAST TRACK TAB ── */
        <div className="grid gap-6 lg:grid-cols-5">
          <div className="lg:col-span-3 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="font-display text-lg font-bold text-gray-900">
              ⚡ Fast-Track Billing
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              Quick ticket with defaults — ₹{settings.defaultPricePerHour}/hr,{" "}
              {settings.defaultDuration} min
            </p>

            <form onSubmit={handleFast} className="space-y-5">
              <InputField label="Parent Name" Icon={User}>
                <input
                  value={fast.parentName || ""}
                  onChange={(e) => {
                    if (validName(e.target.value) || e.target.value === "")
                      setFast((p) => ({ ...p, parentName: e.target.value }));
                  }}
                  placeholder="Enter parent name"
                  className={inputCls}
                />
              </InputField>
              <InputField
                label="Parent Mobile"
                Icon={Phone}
                error={fastErrors.mobile}
              >
                <input
                  value={fast.mobile}
                  onChange={(e) => {
                    if (digitsOnly(e.target.value)) {
                      setFast((p) => ({ ...p, mobile: e.target.value }));
                    }
                  }}
                  placeholder="10-digit mobile number"
                  maxLength={10}
                  className={`${fastErrors.mobile ? inputErrCls : inputCls} text-center text-xl tracking-[0.3em] font-bold h-14`}
                />
              </InputField>

              <PaymentSelector
                value={fast.paymentMode}
                onChange={(v) => setFast((p) => ({ ...p, paymentMode: v }))}
              />
              {fast.paymentMode === "split" && (
                <div className="grid grid-cols-2 gap-3">
                  <InputField label="Cash Amount" error={fastErrors.cashAmount}>
                    <input
                      type="number"
                      value={fast.cashAmount}
                      onChange={(e) => {
                        const cash = cleanNumeric(e.target.value, 0);

                        setFast((p) => ({
                          ...p,
                          cashAmount: cash,
                          upiAmount: Math.max(fastTotal - cash, 0),
                        }));
                      }}
                      className={fastErrors.cashAmount ? inputErrCls : inputCls}
                    />
                  </InputField>

                  <InputField label="UPI Amount" error={fastErrors.upiAmount}>
                    <input
                      type="number"
                      value={fast.upiAmount}
                      readOnly
                      className={fastErrors.upiAmount ? inputErrCls : inputCls}
                    />
                  </InputField>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-purple-600 text-base font-bold text-white hover:bg-purple-700 transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Zap size={18} /> Instant Ticket — ₹{fastTotal.toLocaleString()}
              </button>
            </form>
          </div>

          <div className="lg:col-span-2 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm flex flex-col items-center justify-center gap-4 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-purple-100">
              <Zap size={32} className="text-purple-600" />
            </div>
            <h3 className="font-display text-lg font-bold text-gray-900">
              One-Tap Entry
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed">
              Enter mobile number, select payment mode, and generate a ticket
              instantly.
            </p>
            <div className="w-full rounded-xl bg-gray-50 p-4 text-left text-sm space-y-2">
              {[
                ["Duration", `${settings.defaultDuration} min`],
                ["Rate", `₹${settings.defaultPricePerHour}/hr`],
                ["Subtotal", `₹${fastSubTotal.toFixed(2)}`],
                ["CGST", `${gstRate / 2}% (₹${fastCgstAmount.toFixed(2)})`],
                ["SGST", `${gstRate / 2}% (₹${fastSgstAmount.toFixed(2)})`],
                // ["Total GST", `₹${fastGstAmount.toFixed(2)}`],
                ["Total", `₹${fastTotal.toLocaleString()}`],
              ].map(([l, v], i) => (
                <div key={l} className="flex justify-between">
                  <span className="text-gray-500">{l}</span>
                  <span
                    className={`font-semibold ${i === 2 ? "text-purple-600" : "text-gray-900"}`}
                  >
                    {v}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
